/**
 * createDocumentStore
 *
 * Singleton Valtio store for a Firestore document.
 * Returns the same instance for the same path.
 *
 * **Lifecycle:** Call `connect()` in major components (ArTable, Form).
 * Never call in leaf components (ArCell, FieldUI) — they receive
 * the store from their parent and read via `useSnapshot(store.state)`.
 *
 * **Read:** `useSnapshot(store.state)` for reactive reads. Subscription failures
 * arrive on `state.error`, not on `connect({ onError })` — only the first
 * connector's callback is retained, so every later consumer of a shared path
 * must read the state.
 *
 * **Optimistic write:** mutate `store.state.data` directly.
 * **Persist:** `store.update()` / `store.set()` / `store.setMerge()`.
 *
 * @example
 * ```ts
 * const schema = createDocumentStore<ArTableType>('tableTypes/products');
 * schema.connect();
 *
 * // Read
 * const snap = useSnapshot(schema.state);
 *
 * // Write (consumer updates both)
 * schema.state.data.name = 'New name';
 * schema.update({ name: 'New name' });
 * ```
 */

import type { UpdateData } from 'firebase/firestore';
import type { DocumentState, DocumentPersist, PersistOperationOptions } from '../Store.types';
import { createDocumentState } from '../state/createDocumentState';
import { persistDocument } from '../persist/persistDocument';
import { registry } from './registry';

export type DocumentStoreConnectOptions = {
  onError?: (error: Error) => void;
};

export type DocumentStoreOptions = {
  /**
   * Bypass the registry; return a fresh per-instance store that does not
   * share state with any other consumer of the same `path`. Required for
   * editor / preview scopes that must not bleed optimistic mutations into
   * a connected, user-facing form open in the same JS heap (SYD-1925 #05).
   * Mirrors the same option on `createCollectionStore`.
   */
  standalone?: boolean;
};

export type DocumentStore<T extends Record<string, unknown>> = {
  readonly type: 'document';
  readonly path: string;
  readonly state: DocumentState<T>;
  readonly connected: boolean;
  connect: (options?: DocumentStoreConnectOptions) => Promise<void>;
  disconnect: () => void;
  update: (data: UpdateData<T>, options?: PersistOperationOptions) => Promise<void>;
  set: (data: T, options?: PersistOperationOptions) => Promise<void>;
  setMerge: (data: Partial<T>, options?: PersistOperationOptions) => Promise<void>;
  delete: () => Promise<void>;
};

export function createDocumentStore<T extends Record<string, unknown>>(
  path: string,
  options?: DocumentStoreOptions,
): DocumentStore<T> {
  if (path.includes('__inactive__')) {
    return createInertDocumentStore<T>(path);
  }

  const standalone = options?.standalone ?? false;
  if (!standalone) {
    const existing = registry.get(path);
    if (existing) {
      return existing as DocumentStore<T>;
    }
  }

  const state = createDocumentState<T>();

  let persist: DocumentPersist<T> | null = null;
  let unsubscribe: (() => void) | null = null;
  let refCount = 0;
  let errorHandler: ((error: Error) => void) | undefined;

  const normalizeError = (error: unknown): Error => {
    return error instanceof Error ? error : new Error(String(error));
  };

  const store: DocumentStore<T> & { forceDisconnect: () => void } = {
    type: 'document' as const,
    path,
    state,
    connected: false,

    async connect(connectOptions?) {
      refCount++;
      if (store.connected) return;
      errorHandler = connectOptions?.onError;
      if (!standalone) registry.set(path, store);
      (store as { connected: boolean }).connected = true;
      if (persist) return;
      const result = persistDocument<T>(path, state, connectOptions?.onError);
      persist = result.persist;
      unsubscribe = result.unsubscribe;
    },

    disconnect() {
      refCount = Math.max(0, refCount - 1);
      if (refCount > 0 || !store.connected) return;
      persist?.cancelPending();
      unsubscribe?.();
      persist = null;
      unsubscribe = null;
      errorHandler = undefined;
      state.loading = true;
      state.data = null;
      state.error = null;
      (store as { connected: boolean }).connected = false;
      if (!standalone) registry.delete(path);
    },

    async update(data, updateOptions?) {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      try {
        await persist.update(data, updateOptions);
      } catch (error) {
        const normalized = normalizeError(error);
        errorHandler?.(normalized);
        throw normalized;
      }
    },

    async set(data, setOptions?) {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      try {
        await persist.set(data, setOptions);
      } catch (error) {
        const normalized = normalizeError(error);
        errorHandler?.(normalized);
        throw normalized;
      }
    },

    async setMerge(data, mergeOptions?) {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      try {
        await persist.setMerge(data, mergeOptions);
      } catch (error) {
        const normalized = normalizeError(error);
        errorHandler?.(normalized);
        throw normalized;
      }
    },

    async delete() {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      try {
        await persist.delete();
      } catch (error) {
        const normalized = normalizeError(error);
        errorHandler?.(normalized);
        throw normalized;
      }
    },

    forceDisconnect() {
      refCount = 0;
      store.disconnect();
    },
  };

  if (!standalone) registry.set(path, store);
  return store;
}

function createInertDocumentStore<T extends Record<string, unknown>>(
  path: string,
): DocumentStore<T> {
  const state = createDocumentState<T>();
  state.loading = false;
  state.data = null;

  const inactivePath = path;
  const noop = async (): Promise<void> => {};
  const inertError = async (): Promise<never> => {
    throw new Error(`[store] inert __inactive__ store at "${inactivePath}" rejects writes.`);
  };

  return {
    type: 'document' as const,
    path,
    state,
    connected: false,
    connect: noop,
    disconnect: () => {},
    update: inertError,
    set: inertError,
    setMerge: inertError,
    delete: inertError,
  };
}
